import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { ApiRequestError, normalizeError } from './errors'

/**
 * HTTP 傳輸層。
 *
 * 這一層**不 import vue / pinia / vue-router** —— 純 TypeScript，可獨立測試。
 * 需要跟 auth store 互動的部分透過 `connectAuthBridge()` 由外部注入，
 * 避免 services 反向依賴 stores。
 */

/** 相對路徑：dev 由 vite proxy 轉發，prod 由 nginx 轉發。兩邊前綴一致 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

/** 需要重試的認證錯誤碼 */
const REFRESHABLE_CODES = new Set(['AUTH_TOKEN_EXPIRED', 'AUTH_UNAUTHENTICATED'])

export interface AuthBridge {
  getAccessToken: () => string | null
  /** 執行一次 refresh，成功回新的 access token，失敗 throw */
  refresh: () => Promise<string>
  /** refresh 失敗（refresh token 也死了）時呼叫，用來清狀態並導向登入頁 */
  onSessionLost: () => void
}

let bridge: AuthBridge | null = null

export function connectAuthBridge(next: AuthBridge): void {
  bridge = next
}

/** 標記已重試過，避免無限循環 */
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

/**
 * 同時只允許一個 refresh 在飛。
 *
 * 沒有這個的話，access token 過期瞬間如果有 N 個並行請求同時收到 401，
 * 就會發出 N 次 refresh。而 refresh 有輪替與重用偵測 ——
 * 第二個請求會帶著已被第一個輪替掉的 token，被判定為「重用」，
 * 於是整個 family 被撤銷，使用者莫名其妙被登出。
 */
let inFlightRefresh: Promise<string> | null = null

function refreshOnce(): Promise<string> {
  if (bridge === null) {
    return Promise.reject(
      new ApiRequestError({ code: 'UNKNOWN', message: 'Auth bridge is not connected' }),
    )
  }

  inFlightRefresh ??= bridge.refresh().finally(() => {
    inFlightRefresh = null
  })

  return inFlightRefresh
}

export const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  // refresh token 走 httpOnly cookie，沒有這個瀏覽器不會帶上
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

http.interceptors.request.use((config) => {
  const token = bridge?.getAccessToken() ?? null
  if (token !== null) config.headers.Authorization = `Bearer ${token}`
  return config
})

http.interceptors.response.use(
  (response) => response,
  async (rawError: unknown) => {
    const error = normalizeError(rawError)
    const config = (rawError as { config?: RetriableConfig }).config

    const canRetry =
      config !== undefined &&
      config._retried !== true &&
      error.status === 401 &&
      REFRESHABLE_CODES.has(error.code) &&
      // refresh 端點自己失敗時不要再遞迴 refresh
      config.url?.includes('/auth/refresh') !== true

    if (!canRetry) throw error

    try {
      const token = await refreshOnce()
      config._retried = true
      config.headers.Authorization = `Bearer ${token}`
      return await http.request(config)
    } catch {
      bridge?.onSessionLost()
      throw error
    }
  },
)

/** 解開統一回應信封，只把 `data` 交給呼叫端 */
export async function request<T>(config: Parameters<AxiosInstance['request']>[0]): Promise<T> {
  const response = await http.request<{ ok: true; data: T }>(config)
  return response.data.data
}
